import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import type { IClientShopFlags } from "@voith-webshop/core-interfaces";
import { ShopDeploymentEnv } from "@voith-webshop/core-interfaces";

import i18nConfig from "@/shop/config/i18next.json";
import type { IDebitorInfo, ISessionData, ISessionUserData } from "@/shop/types/session";

import type { ISessionState } from "./types";

const getSessionState: Selector<{ session: ISessionState }, ISessionState> = (state) => state && state.session;

const fallbackSessionData: ISessionData = {
  user: null!,
  industry: null!,
  selectedDebitor: null!,
  availableDebitors: [],
  webshopLanguage: i18nConfig.fallbackLng,
  webshopLanguages: [i18nConfig.fallbackLng],
  flags: {} as IClientShopFlags,
  isLoggedIn: false,
  deploymentEnv: null!,
  deploymentStage: null!,
  deploymentSystemNote: null,
  permissions: {},
  analyticsSiteId: null!
};

export const getSessionData = createSelector(getSessionState, (session) => session.sessionData ?? fallbackSessionData);

// session metadata

export const getIsSessionSyncing = createSelector(getSessionState, (state) => state.isSyncing);

// cxml session data

export const getCXmlSessionData = createSelector(getSessionData, (sessionData) => sessionData.cxmlSessionData ?? null);

// debitors

export const getAvailableDebitors = createSelector(getSessionData, (sessionData) => sessionData.availableDebitors);

export const getSelectedDebitor = createSelector(getSessionData, (sessionData) => sessionData.selectedDebitor);

export const getSelectedDebitorNumber = createSelector(getSelectedDebitor, (debitor: IDebitorInfo) => debitor?.number);

export const getAvailableLanguages = createSelector(getSessionData, (sessionData) => sessionData.webshopLanguages);

export const getSelectedLanguage = createSelector(getSessionData, (sessionData) => sessionData.webshopLanguage);

// user

export const getUser = createSelector(getSessionData, (session): ISessionUserData => session.user);

export const getUserFirstName = createSelector(getUser, (user) => (user ? user.firstName : ""));

export const getUserLastName = createSelector(getUser, (user) => (user ? user.lastName : ""));

export const getUserDisplayName = createSelector(getUser, (user) => (user ? (user ? user.firstName + " " + user.lastName : "") : null));

export const getUserLogin = createSelector(getUser, (user) => (user ? user.login : ""));

export const getIsTestUser = createSelector(getUser, (user) => (user && user.isTestUser ? true : false));

export const getPermissions = createSelector(getSessionData, (sessionData) => (sessionData ? sessionData.permissions : {}));

export const getSessionFlags = createSelector(getSessionData, (sessionData): IClientShopFlags => sessionData.flags ?? ({} as IClientShopFlags));

// isolated session flags
export const getShowIbaseEdocLink = createSelector(getSessionFlags, (flags) => flags.showIbaseEdocLink);
export const getShowFavList = createSelector(getSessionFlags, (flags) => flags.showFavList);
export const getShowContact = createSelector(getSessionFlags, (flags) => flags.showContact);
export const getShowFeedback = createSelector(getSessionFlags, (flags) => flags.showFeedback);
export const getShowClickableDrawings = createSelector(getSessionFlags, (flags) => flags.showClickableDrawings);
export const getIsLoyaltyProgramEnabled = createSelector(getSessionFlags, (flags) => flags.enableLoyaltyPoints);
export const getIsAssemblyDateEnabled = createSelector(getSessionFlags, (flags) => flags.enableAssemblyDate);
export const getEnableMaterialListParsing = createSelector(getSessionFlags, (flags) => flags.enableMaterialListParsing);
export const getShowEDocLink = createSelector(getSessionFlags, (flags) => flags.showEdocLink);

export const getIsLoggedIn = createSelector(getSessionData, (snData) => snData.isLoggedIn);

export const getIndustry = createSelector(getSessionData, (snData) => (snData ? snData.industry : null));

/**
 * Same as {@link getSessionFlags}
 */
export const getIndustryFeatures = getSessionFlags;

/**
 * Get the deployment environment of the shop
 */
export const getDeploymentEnv = createSelector(getSessionData, (sessionData) => sessionData.deploymentEnv);

/**
 * Get the deployment stage of the shop
 */
export const getDeploymentStage = createSelector(getSessionData, (sessionData) => sessionData.deploymentStage);

/**
 * Detect if the shop is open or not. Should work for now
 * - Non cloud shops are hosted in *.apps.voith.com
 * - China cloud shop is hosted in *.cn.voith.com
 * - localhost is unaffected
 * @returns
 */
export const getIsOpenShop = createSelector(getDeploymentEnv, (deploymentEnv) => deploymentEnv !== ShopDeploymentEnv.Intranet);

/**
 * Retrieve deployment system note from session data
 */
export const getDeploymentSystemNote = createSelector(getSessionData, (sessionData) => sessionData.deploymentSystemNote);

/**
 * Get analytics site id
 */
export const getAnalyticsSiteId = createSelector(getSessionData, (sessionData) => sessionData.analyticsSiteId);
